// Pure model for the APPLICANT side of an intake form (Phase 5/6). No React,
// no I/O — the answer/consent state shape plus completeness checks and the
// wire-payload builders. Shared by the member join modal (paths A/B) and the
// Circle→Network application flow (path C), so it stays effect-free and
// unit-tested without a DOM.

import type { components } from '@leanin/api-client';
import { MAX_ANSWER_CHARS } from '@leanin/shared/intake';

export type ApplicantIntakeForm = NonNullable<
  components['schemas']['ApplicantIntakeFormView']['form']
>;
export type ApplicantQuestion = ApplicantIntakeForm['questions'][number];

/** A declaration answer is a boolean; every other type is a string. */
export type AnswerValue = string | boolean;
export type AnswerState = Readonly<Record<string, AnswerValue>>;

/** Wire shapes for the submit body (`CreateJoinRequest` / `CircleApplicationRequest`). */
export type AnswerPayload = { questionId: string; value: AnswerValue };
export type ConsentPayload = { statementsAgreed: string[] };

/**
 * Profile-derived values used to prefill recommended region/sector questions
 * (a leader adds a `prefillSource` question; the applicant's profile seeds the
 * initial answer). Keys mirror `IntakeQuestion.prefillSource`.
 */
export type IntakePrefillValues = Partial<Record<'region' | 'sector', string>>;

export function initAnswers(
  form: ApplicantIntakeForm,
  prefill: IntakePrefillValues = {},
): AnswerState {
  return mergeAnswers(form, {}, prefill);
}

/**
 * Answer state for `form`, carrying over any prior value whose `questionId`
 * still exists (question ids are stable across versions). Used both on first
 * load (prev = {}) and after a stale-version refetch, so the applicant doesn't
 * lose typed answers when the leader edits the form mid-fill (edge case 2).
 * A freshly-seeded question (no prior value) with a `prefillSource` takes its
 * initial value from the applicant's profile (`prefill`). An untouched,
 * still-blank prefill field is also (re)seeded when the profile value arrives
 * after the first seed — the profile query can resolve after the form loads —
 * with `touched` distinguishing a field the applicant deliberately cleared. A
 * touched or non-empty answer is never overwritten.
 */
export function mergeAnswers(
  form: ApplicantIntakeForm,
  prev: AnswerState,
  prefill: IntakePrefillValues = {},
  touched?: ReadonlySet<string>,
): AnswerState {
  const next: Record<string, AnswerValue> = {};
  for (const q of form.questions) {
    const existing = prev[q.id];
    if (existing === undefined) {
      next[q.id] = seedValue(q, prefill);
      continue;
    }
    const reseedable =
      existing === '' &&
      (q.type === 'short_answer' || q.type === 'long_answer') &&
      q.prefillSource != null &&
      (touched === undefined || !touched.has(q.id));
    next[q.id] = reseedable ? seedValue(q, prefill) : existing;
  }
  return next;
}

/**
 * The initial value for a not-yet-answered question. Declarations start
 * unchecked; free-text region/sector questions are prefilled from the profile
 * when available. Prefill applies only to `short_answer`/`long_answer` — a
 * `single_choice` is a controlled selection that can't take an arbitrary
 * profile string, so it always starts empty.
 */
function seedValue(
  question: ApplicantQuestion,
  prefill: IntakePrefillValues,
): AnswerValue {
  if (question.type === 'declaration') return false;
  if (
    (question.type === 'short_answer' || question.type === 'long_answer') &&
    question.prefillSource != null
  ) {
    const value = prefill[question.prefillSource];
    if (value !== undefined && value.trim().length > 0) return value.trim();
  }
  return '';
}

/** Whether a question's current value satisfies it (for required-gating). */
export function answerComplete(
  question: ApplicantQuestion,
  value: AnswerValue | undefined,
): boolean {
  if (question.type === 'declaration') return value === true;
  return typeof value === 'string' && value.trim().length > 0;
}

export function answerTooLong(value: AnswerValue | undefined): boolean {
  return typeof value === 'string' && value.length > MAX_ANSWER_CHARS;
}

/**
 * Count of required items still missing = unanswered required questions +
 * un-agreed consent statements (consent is always required — R7). Drives the
 * live missing-count and submit-disabled-until-complete.
 */
export function missingRequiredCount(
  form: ApplicantIntakeForm,
  answers: AnswerState,
  agreed: ReadonlySet<string>,
): number {
  let missing = 0;
  for (const q of form.questions) {
    if (q.required && !answerComplete(q, answers[q.id])) missing += 1;
  }
  for (const key of form.consentStatementKeys) {
    if (!agreed.has(key)) missing += 1;
  }
  return missing;
}

export function anyAnswerTooLong(
  form: ApplicantIntakeForm,
  answers: AnswerState,
): boolean {
  return form.questions.some((q) => answerTooLong(answers[q.id]));
}

export function isSubmittable(
  form: ApplicantIntakeForm,
  answers: AnswerState,
  agreed: ReadonlySet<string>,
): boolean {
  return (
    missingRequiredCount(form, answers, agreed) === 0 &&
    !anyAnswerTooLong(form, answers)
  );
}

/**
 * The `answers` array for the submit body. Declarations always emit a boolean;
 * short/long/single_choice emit a trimmed string only when non-empty (blank
 * optional answers are dropped, matching the server's snapshot).
 */
export function buildAnswers(
  form: ApplicantIntakeForm,
  answers: AnswerState,
): AnswerPayload[] {
  const out: AnswerPayload[] = [];
  for (const q of form.questions) {
    const v = answers[q.id];
    if (q.type === 'declaration') {
      out.push({ questionId: q.id, value: v === true });
    } else if (typeof v === 'string') {
      // single_choice is a controlled selection — send the chosen option
      // byte-for-byte so it matches the server's stored option; short/long are
      // free text and get trimmed.
      const value = q.type === 'single_choice' ? v : v.trim();
      if (value.length > 0) out.push({ questionId: q.id, value });
    }
  }
  return out;
}

export function buildConsent(agreed: ReadonlySet<string>): ConsentPayload {
  return { statementsAgreed: [...agreed] };
}
